import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterLink } from '@angular/router';
import { DialogService, TurnoService, PermissoesService } from 'app/services';
import { LoadingComponent, FiltroClientesComponent } from 'app/components';
import { finalize } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConverterBRTPipe } from 'app/pipes/converterBRT/converter-brt.pipe';
import { Permissoes, Turno, Turnos } from 'app/interfaces';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-turnos',
  standalone: true,
  imports: [MatTableModule, FiltroClientesComponent, CommonModule, RouterLink, LoadingComponent, MatTooltipModule, ConverterBRTPipe, TranslocoModule],
  templateUrl: './listar-turnos.component.html',
  styleUrl: './listar-turnos.component.css'
})

export class ListarTurnosComponent implements OnInit {
  public dataSource: MatTableDataSource<Turno> = new MatTableDataSource();
  public loading: boolean = true;
  public displayedColumns: string[] = ['nome', 'horario_inicio', 'horario_fim', 'status'];
  public turnos: Turnos = [];
  public permissoes: Permissoes.Acessos = {
    criar: false,
    editar: false,
    excluir: false,
    exibirAcoes: false
  }

  public readonly PERMISSAO_CRIAR = 'criar_turnos_premoplan';
  public readonly PERMISSAO_EDITAR = 'editar_turnos_premoplan';
  public readonly PERMISSAO_EXCLUIR = 'excluir_turnos_premoplan';

  constructor(
    private permissoesService: PermissoesService,
    public turnoService: TurnoService,
    private dialogService: DialogService,
    private translocoService: TranslocoService
  ) { }

  ngOnInit(): void {
    this.verificarPermissoes();
  }

  public verificarPermissoes(): void {
    this.permissoes.criar = this.permissoesService.usuarioTemPermissao(this.PERMISSAO_CRIAR);
    this.permissoes.editar = this.permissoesService.usuarioTemPermissao(this.PERMISSAO_EDITAR);
    this.permissoes.excluir = this.permissoesService.usuarioTemPermissao(this.PERMISSAO_EXCLUIR);
    this.permissoes.exibirAcoes = this.permissoes.editar || this.permissoes.excluir;

    if (this.permissoes.exibirAcoes && !this.displayedColumns.includes('acoes')) {
      this.displayedColumns.push('acoes');
    }
  }

  public buscarTurnos(clienteId: number): void {
    this.dataSource.data = [];
    this.loading = true;
    this.turnoService.buscarTodosPorCliente(clienteId)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
          next: (turnos: Turnos) => {
            this.turnos = turnos;
            this.dataSource.data = turnos;
          },
          error: () => {
            this.dialogService.erro(
              this.translocoService.translate('COMPONENTS.TURNOS_LIST.dialogs.loadErrorTitle'),
              this.translocoService.translate('COMPONENTS.TURNOS_LIST.dialogs.loadErrorMessage')
            );
            this.dataSource.data = [];
          }
      });
  }

  public async alterarEstadoTurno(turno: Turno): Promise<void> {
    const acaoKey = turno.bl_ativo ? 'deactivate' : 'activate';

    const confirmado = await this.dialogService.confirmacao(
      this.translocoService.translate('COMPONENTS.TURNOS_LIST.dialogs.changeStatusTitle'),
      this.translocoService.translate(
        `COMPONENTS.TURNOS_LIST.dialogs.changeStatusConfirm.${acaoKey}`
      )
    );

    if (!confirmado) return;

    this.dialogService.loading(
      this.translocoService.translate(
        `COMPONENTS.TURNOS_LIST.dialogs.changing.${acaoKey}`
      )
    );

    this.turnoService.alterarEstado(turno.sr_id!).subscribe({
      next: (turnosAtualizados: Turnos) => {
        this.dialogService.sucesso(
          this.translocoService.translate('COMPONENTS.TURNOS_LIST.dialogs.successTitle'),
          this.translocoService.translate('COMPONENTS.TURNOS_LIST.dialogs.successMessage')
        );

        this.turnos = turnosAtualizados;
        this.dataSource.data = turnosAtualizados;
      },
      error: () => {
        this.dialogService.erro(
          this.translocoService.translate('COMPONENTS.TURNOS_LIST.dialogs.errorTitle'),
          this.translocoService.translate('COMPONENTS.TURNOS_LIST.dialogs.errorMessage')
        );
      }
    });
  }


  public atualizarTabela(turnoAtualizado: Turno | number): void {
    if (typeof turnoAtualizado === 'number') {
      this.turnos = this.turnos.filter(turno => turno.sr_id !== turnoAtualizado);
    } else {
      const indice = this.turnos.findIndex(turno => turno.sr_id === turnoAtualizado.sr_id);
      if (indice !== -1) {
        this.turnos[indice] = turnoAtualizado;
      }
    }
    this.dataSource.data = this.turnos;
  }

  public aplicarFiltro(event: Event) {
    let valorFiltrado: string = (event.target as HTMLInputElement).value;
    this.dataSource.filter = valorFiltrado;
    this.dataSource.filterPredicate = (data: Turno, filter: string) => {
      const dataNome = this.formataTextoFiltrado(data.vc_nome);
      const filtroNome = this.formataTextoFiltrado(filter);
      return dataNome.includes(filtroNome);
    };
  }

  private formataTextoFiltrado(texto: string): string {
    return texto.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }

  public async excluir(turnoId: number): Promise<void> {
    const confirmou = await this.dialogService.confirmacao(
      this.translocoService.translate('COMPONENTS.TURNOS_LIST.dialogs.deleteTitle'),
      this.translocoService.translate('COMPONENTS.TURNOS_LIST.dialogs.deleteConfirm')
    );

    if (!confirmou) return;

    this.dialogService.loading(
      this.translocoService.translate('COMPONENTS.TURNOS_LIST.dialogs.deleting')
    );

    this.turnoService.deletar(turnoId).subscribe({
      next: async () => {
        await this.dialogService.sucesso(
          this.translocoService.translate('COMPONENTS.TURNOS_LIST.dialogs.successTitle'),
          this.translocoService.translate('COMPONENTS.TURNOS_LIST.dialogs.deleteSuccess')
        );
        this.atualizarTabela(turnoId);
      },
      error: () => {
        this.dialogService.erro(
          this.translocoService.translate('COMPONENTS.TURNOS_LIST.dialogs.errorTitle'),
          this.translocoService.translate('COMPONENTS.TURNOS_LIST.dialogs.deleteError')
        );
      }
    });
  }
}
